import { useState, useEffect, useRef, useCallback } from "react";
import { C, S } from "../customers/theme";

// Pure client-side receipt capture. Requires a SECURE CONTEXT (works on localhost;
// needs HTTPS on the live domain). Degrades gracefully: unsupported / no camera /
// permission-denied all fall back to a clear message so the caller's Upload path
// still works — the form can never break.
export default function CameraCapture({ onCapture, onClose }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [facing, setFacing] = useState("environment"); // back camera by default (receipts)
  const [shot, setShot] = useState(null);              // captured still as a data URL
  const [error, setError] = useState("");
  const supported = typeof navigator !== "undefined" && !!navigator.mediaDevices?.getUserMedia;

  const stop = useCallback(() => {
    if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
  }, []);

  const start = useCallback(async (mode) => {
    setError(""); stop();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: mode }, audio: false });
      streamRef.current = stream;
      if (videoRef.current) { videoRef.current.srcObject = stream; try { await videoRef.current.play(); } catch { /* autoplay quirk */ } }
    } catch (e) {
      const name = e?.name;
      setError(name === "NotAllowedError" ? "Camera access was blocked. You can upload a photo instead."
        : name === "NotFoundError" || name === "DevicesNotFoundError" ? "No camera found. Upload a photo instead."
        : "Could not start the camera. Upload a photo instead.");
    }
  }, [stop]);

  useEffect(() => {
    if (!supported) { setError("Camera isn't available on this device. Upload a photo instead."); return undefined; }
    start(facing);
    return stop;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const flip = () => { const next = facing === "environment" ? "user" : "environment"; setFacing(next); start(next); };

  const capture = () => {
    const v = videoRef.current; if (!v) return;
    const canvas = document.createElement("canvas");
    canvas.width = v.videoWidth || 720; canvas.height = v.videoHeight || 960;
    canvas.getContext("2d").drawImage(v, 0, 0, canvas.width, canvas.height);
    setShot(canvas.toDataURL("image/jpeg", 0.9));
    stop();
  };
  const retake = () => { setShot(null); start(facing); };
  const use = () => {
    const arr = shot.split(","); const bstr = atob(arr[1]);
    let n = bstr.length; const u8 = new Uint8Array(n); while (n--) u8[n] = bstr.charCodeAt(n);
    const file = new File([u8], `receipt-${Date.now()}.jpg`, { type: "image/jpeg" });
    stop(); onCapture(file);
  };
  const close = () => { stop(); onClose(); };

  return (
    <div style={S.modal} onClick={(e) => e.target === e.currentTarget && close()}>
      <div style={{ ...S.modalBox, width: 480, textAlign: "center" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>Capture receipt</h2>
          <button onClick={close} aria-label="Close" style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: C.textMuted }}>✕</button>
        </div>
        {error ? (
          <div role="alert" style={{ color: C.warning, fontSize: 13, padding: "24px 8px", lineHeight: 1.6 }}>{error}</div>
        ) : shot ? (
          <img src={shot} alt="Captured receipt" style={{ width: "100%", borderRadius: 8, maxHeight: "52vh", objectFit: "contain", background: "#000" }} />
        ) : (
          <video ref={videoRef} playsInline muted aria-label="Camera preview" style={{ width: "100%", borderRadius: 8, maxHeight: "52vh", objectFit: "cover", background: "#000" }} />
        )}
        <div style={{ display: "flex", gap: 10, justifyContent: "center", marginTop: 14, flexWrap: "wrap" }}>
          {!error && !shot && (
            <>
              <button style={S.btn()} onClick={capture}>📸 Capture</button>
              <button style={S.btn("secondary")} onClick={flip} title="Switch front/back camera">🔄 Flip</button>
            </>
          )}
          {shot && (
            <>
              <button style={S.btn("secondary")} onClick={retake}>Retake</button>
              <button style={S.btn()} onClick={use}>Use photo</button>
            </>
          )}
          <button style={S.btn("ghost")} onClick={close}>Cancel</button>
        </div>
      </div>
    </div>
  );
}
